import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { get } from "lodash";
import { reduxForm, Field, reset } from "redux-form";
import { NotificationManager } from "react-notifications";

import { FormField } from "../../../../sharedComponents/formField";
import { searchIngredients, getIngredientWeightsList, resetField } from "../../../../store/actions";
import { required } from "../../../../constants/formValidators";
import PaginatedTable from "../../../../sharedComponents/tableWithPagination";
import { RecipeKeys } from "../../../../constants/recipeConstants";

const SelectIngredientsForm = ({
	recipe,
	handleSubmit,
	isExists,
	setIsExists,
	searchIngredientResults,
	searchIngredientTotal,
	searchIngredientPrev,
	searchIngredientNext,
	togglePanel,
	isSearching,
	searchIngredients,
	getIngredientWeightsList,
	ingredientWeights,
	onSubmit,
	onIngredientSelect,
	resetForm,
	resetField,
	isAdmin
}) => {
	const [selectedIngredient, setSelectedIngredient] = useState({});

	useEffect(() => {
		if (selectedIngredient.id) {
			getIngredientWeightsList(selectedIngredient.id);
		}
	}, [selectedIngredient, getIngredientWeightsList]);

	useEffect(() => {
		return () => {
			resetField("AddRecipe.searchIngredientResults", {});
		};
	}, [resetField]);

	const selectIngredientsColumns = [
		{
			Header: "Select",
			accessor: "id",
			Cell: (props) => (
				<div className="form-group m-0 false">
					<label className="form-label m-0">
						<input
							name="selectIngredientToAdd"
							className="form-control"
							type="radio"
							value={props.value}
							onChange={() => onSelectIngredient(props.original)}
							checked={selectedIngredient.id === props.value}
						/>
						<span className="form-label-span" />
					</label>
				</div>
			),
			width: 100,
			sortable: false
		},
		{
			Header: "Ingredient",
			accessor: "label"
		}
	];

	const onSelectIngredient = (ingredient) => {
		setIsExists(false);
		setSelectedIngredient(ingredient);
		onIngredientSelect && onIngredientSelect(ingredient);
	};

	const validateForm = (values) => {
		const measure_data =
			values && values.measure ? ingredientWeights.filter((item) => item.id === values.measure)[0] : null;
		if (!measure_data) {
			NotificationManager.error("Please select weight!", "", 5000);
		} else if (values && values.measure) {
			const parameters = {
				id: null,
				ingredient: selectedIngredient.id,
				ingredient_data: {
					id: selectedIngredient.id,
					name: null
				},
				main: values.main ? values.main : false,
				measure: values.measure ? Number(values.measure) : null,
				measure_data: measure_data || {},
				quantity: values.qty,
				recipe: recipe.id,
				seq: values.seq
			};
			onSubmit(parameters);
			resetForm("SelectIngredientsForm");
		} else {
			NotificationManager.error("Please select another ingredient!", "", 5000);
		}
	};

	return (
		<form onSubmit={handleSubmit(validateForm)} className="d-flex w-100 flex-column flex-fill">
			<div className="flex-fill">
				{searchIngredientResults && searchIngredientResults.length ? (
					<React.Fragment>
						<h5 className="text-primary-dark mt-2 mb-2">Pick the ingredient from the list</h5>
						<PaginatedTable
							data={searchIngredientResults}
							columns={selectIngredientsColumns}
							pageSize={RecipeKeys.SEARCH_INGREDIENT_RESULTS_LIMIT}
							totalItems={searchIngredientTotal}
							prev={searchIngredientPrev}
							next={searchIngredientNext}
							onPagination={(event) => searchIngredients(event)}
						/>
					</React.Fragment>
				) : searchIngredientResults !== null ? (
					<div className="text-center alert alert-info mb-4">No ingredients found...</div>
				) : isSearching ? (
					<div className="text-center alert alert-info mb-4">Searching Ingredients...</div>
				) : null}
				{selectedIngredient && selectedIngredient.id ? (
					<div className="ingredients-form">
						<h5 className="text-primary-dark mt-4 mb-2">Decide how much of this ingredient</h5>
						<div className="row">
							<div className="col-1">
								<label className="form-label m-0">Main</label>
								<Field name="main" type="checkbox" formGroupClass="m-0 mt-2" component={FormField} />
							</div>
							<div className="col-3">
								<Field
									name="seq"
									label="Seq"
									type="number"
									min="1"
									formGroupClass="m-0"
									component={FormField}
									validate={[required]}
								/>
							</div>
							<div className="col-3">
								<Field
									name="qty"
									label="Quantity"
									type="number"
									min="0.01"
									step="0.01"
									formGroupClass="m-0"
									component={FormField}
									validate={[required]}
								/>
							</div>
							<div className="col-5">
								<Field
									name="measure"
									label="Weights"
									type="select"
									options={ingredientWeights}
									viewValue={"measure"}
									formGroupClass="m-0"
									component={FormField}
									validate={[required]}
								/>
							</div>
						</div>
					</div>
				) : null}
			</div>
			<div className="d-flex align-items-end ingredients-footer">
				<div className="d-flex flex-row align-items-center w-100">
					{isAdmin ? (
						<div className="text-center">
							<h5 className="text-primary-dark mt-3">Cannot find an ingredient? </h5>
							<a
								href="/nutrition/food-database/add"
								className="btn btn-primary-dark btn-ghost"
								rel="noopener noreferrer"
								target="_blank">
								<i className="icon icon-add-edit" /> Edit or Add
							</a>
						</div>
					) : (
							" "
						)}
					<div className="flex-fill text-right">
						<button className="btn btn-default" type="button" onClick={() => togglePanel(false)}>
							Cancel
						</button>
						<button className="btn btn-primary-dark" type="submit" disabled={isExists || !selectedIngredient.id}>
							Add Now
						</button>
					</div>
				</div>
			</div>
		</form>
	);
};

const mapStateToProps = (state) => {
	return {
		searchIngredientResults: get(state, "AddRecipe.searchIngredientResults.results", null),
		searchIngredientTotal: get(state, "AddRecipe.searchIngredientResults.count", 0),
		searchIngredientPrev: get(state, "AddRecipe.searchIngredientResults.previous", null),
		searchIngredientNext: get(state, "AddRecipe.searchIngredientResults.next", null),
		ingredientWeights: get(state, "AddRecipe.getIngredientWeights.results", []),
		recipeIngredientList: get(state, "AddRecipe.recipeIngredientList.results", []),
		isAdmin: state.Auth.isAdmin
	};
};

const mapDispatchToProps = {
	searchIngredients,
	getIngredientWeightsList,
	resetForm: reset,
	resetField
};

export default connect(
	mapStateToProps,
	mapDispatchToProps
)(
	reduxForm({
		form: "SelectIngredientsForm",
		enableReinitialize: true
	})(SelectIngredientsForm)
);
